import React from "react";
import {
  Autocomplete,
  TextField,
  FormControl,
  Typography,
  createFilterOptions,
} from "@mui/material";

interface SearchableSelectInputProps {
  field: string;
  formData: any;
  onChange: (field: string, value: string) => void;
  options: string[] | { label: string; value: string }[];
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
  loading?: boolean;
}

const SearchableSelectInput: React.FC<SearchableSelectInputProps> = ({
  field,
  formData,
  onChange,
  options,
  label,
  required = false,
  disabled = false,
  error,
  placeholder,
  loading = false,
}) => {
  // Convert options to the format expected by Autocomplete (string values for reliable matching)
  const autocompleteOptions = options.map((opt) => {
    const isObject = typeof opt === "object";
    return {
      label: String(isObject ? (opt as { label: string }).label ?? "" : opt),
      value: String(isObject ? (opt as { value: string }).value ?? "" : opt),
    };
  });

  const selectedValue = String(formData[field] ?? "");
  const selectedOption = autocompleteOptions.find((opt) => opt.value === selectedValue) ?? null;

  const filterOptions = createFilterOptions<{
    label: string;
    value: string;
  }>({
    matchFrom: "any",
    ignoreCase: true,
    stringify: (option) => `${option.label} ${option.value}`,
  });

  return (
    <FormControl 
    fullWidth 
    required={required} 
    error={!!error}
    sx={{
      "& .MuiInputLabel-asterisk": {
        color: "red !important",
      },
    }}
  >
      <Autocomplete
        value={selectedOption}
        onChange={(_, newValue) => {
          onChange(field, newValue != null ? String(newValue.value) : "");
        }}
        options={autocompleteOptions}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) =>
          option != null && value != null && String(option.value) === String(value.value)
        }
        filterOptions={filterOptions}
        disabled={disabled || loading}
        loading={loading}
        loadingText="Loading options..."
        noOptionsText="No options found"
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder || `Select ${label?.toLowerCase() || "option"}`}
            size="small"
            required={required}
            error={!!error}
            helperText={error}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? "⏳" : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                minHeight: 41,
                '& fieldset': {
                  transition: 'border-color 0.2s ease',
                },
                '&.Mui-error fieldset': {
                  borderColor: '#d32f2f',
                },
                '&.Mui-error:hover fieldset': {
                  borderColor: '#d32f2f',
                },
                '&.Mui-focused.Mui-error fieldset': {
                  borderColor: '#d32f2f',
                  borderWidth: 2,
                },
              },
              '& .MuiInputBase-input': {
                paddingTop: '10px',
                paddingBottom: '10px',
              },
              '& .MuiFormLabel-asterisk': {
                color: 'red',
              },
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            <Typography variant="body2" noWrap>
              {option.label}
            </Typography>
          </li>
        )}
        sx={{
          "& .MuiAutocomplete-inputRoot": {
            minHeight: 42,
            "& .MuiAutocomplete-input": {
              fontSize: "0.875rem",
            },
          },
          "& .MuiFormLabel-root": {
            "&.MuiInputLabel-asterisk": {
              color: "red !important",
            },
            "& .MuiInputLabel-asterisk": {
              color: "red !important",
            },
          },
        }}
        ListboxProps={{
          style: {
            maxHeight: 200,
            fontSize: "0.875rem",
          },
        }}
        slotProps={{
          paper: {
            sx: {
              "& .MuiAutocomplete-listbox": {
                maxHeight: 200,
                "& .MuiAutocomplete-option": {
                  fontSize: "0.875rem",
                  padding: "8px 16px",
                  "&:hover": {
                    backgroundColor: "rgba(13, 44, 90, 0.08)",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "rgba(13, 44, 90, 0.12)",
                  },
                },
              },
            },
          },
        }}
      />
    </FormControl>
  );
};

export default SearchableSelectInput;
